import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, liveStatusTranslationKey, notificationMessagesTopic } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { NotificationMessageSummary } from './notification.models';
import { NotificationService } from './notification.service';
import { normalizeNotificationCategory, normalizeNotificationChannel } from './notification.utils';

@Component({
  standalone: true,
  selector: 'ain-notifications-messages',
  imports: [
    AinPageComponent, AinStateComponent, InfoHintComponent, AinFormFieldComponent,
    FormsModule, TableModule, ButtonModule, InputTextModule, SelectModule,
    JalaliDateInputComponent, AinDatePipe
  ],
  template: `
    <ain-page
      [eyebrow]="t('notifications.eyebrow')"
      [title]="t('notifications.messages.title')"
      [description]="t('notifications.messages.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'notifications.messages.help.title'"
        [descriptionKey]="'notifications.messages.help.description'"
        [bulletKeys]="['notifications.messages.help.bullets.filters','notifications.messages.help.bullets.detail','notifications.messages.help.bullets.live']" />

      @if (noPermission()) {
        <ain-state type="empty" [title]="t('common.noPermission')" />
      } @else {
        <section class="card">
          <div class="toolbar">
            <button pButton type="button" severity="secondary" [label]="t('catalog.action.refresh')" (click)="refresh()"></button>
            <span class="live-badge" [class]="liveUpdateService.connectionState()">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
          </div>
          <div class="filters">
            <ain-form-field [label]="t('notifications.messages.filters.search')">
              <input pInputText [(ngModel)]="filters.search" (keyup.enter)="applyFilters()" />
            </ain-form-field>
            <ain-form-field [label]="t('notifications.messages.filters.channel')">
              <p-select [options]="channelOptions" optionLabel="label" optionValue="value" [(ngModel)]="filters.channel" [showClear]="true" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('notifications.messages.filters.category')">
              <p-select [options]="categoryOptions" optionLabel="label" optionValue="value" [(ngModel)]="filters.category" [showClear]="true" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('notifications.messages.filters.status')">
              <p-select [options]="statusOptions" optionLabel="label" optionValue="value" [(ngModel)]="filters.status" [showClear]="true" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('notifications.messages.filters.provider')">
              <p-select [options]="providerOptions" optionLabel="label" optionValue="value" [(ngModel)]="filters.providerKey" [showClear]="true" appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('notifications.messages.filters.fromDate')">
              <ain-date-input [(ngModel)]="filters.fromDate" />
            </ain-form-field>
            <ain-form-field [label]="t('notifications.messages.filters.toDate')">
              <ain-date-input [(ngModel)]="filters.toDate" />
            </ain-form-field>
            <button pButton type="button" [label]="t('catalog.action.search')" (click)="applyFilters()"></button>
          </div>
        </section>

        @if (initialLoading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else if (error()) {
          <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" />
        } @else if (rows().length === 0) {
          <ain-state type="empty" [title]="t('notifications.messages.empty')" />
        } @else {
          <section class="card">
            <p-table
              [value]="rows()"
              dataKey="id"
              [paginator]="true"
              [rows]="pageSize"
              [first]="tableFirst()"
              [totalRecords]="totalCount()"
              [lazy]="true"
              [loading]="tableLoading()"
              [selection]="selectedRows()"
              (selectionChange)="selectedRows.set($event)"
              (onLazyLoad)="onLazyLoad($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th>{{ t('notifications.messages.columns.subject') }}</th>
                  <th>{{ t('notifications.messages.columns.channel') }}</th>
                  <th>{{ t('notifications.messages.columns.category') }}</th>
                  <th>{{ t('notifications.messages.columns.provider') }}</th>
                  <th>{{ t('notifications.messages.columns.status') }}</th>
                  <th>{{ t('notifications.messages.columns.createdAt') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.subject || row.messageKey }}</button></td>
                  <td>{{ channelLabel(row.channel) }}</td>
                  <td>{{ categoryLabel(row.category) }}</td>
                  <td>{{ row.providerKeys.length ? row.providerKeys.join(', ') : '—' }}</td>
                  <td>{{ statusLabel(row.status) }}</td>
                  <td>{{ row.createdAtUtc | ainDate:'datetime' }}</td>
                </tr>
              </ng-template>
            </p-table>
          </section>
        }
      }
    </ain-page>
  `,
  styles: [`
    .card { border:1px solid #e5e7eb; border-radius:12px; padding:12px; margin-bottom:12px; }
    .toolbar { display:flex; gap:8px; align-items:center; flex-wrap:wrap; margin-bottom:10px; }
    .filters { display:grid; gap:10px; grid-template-columns:repeat(auto-fit,minmax(180px,1fr)); align-items:end; }
    .link-btn { border:0; background:none; color:#1d4ed8; cursor:pointer; font-weight:600; padding:0; }
    .live-badge { font-size:0.85rem; padding:0.2rem 0.5rem; border-radius:999px; border:1px solid #d1d5db; }
    .live-badge.connected { border-color:#16a34a; color:#166534; }
  `]
})
export class NotificationsMessagesComponent implements OnInit {
  private readonly api = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly i18n = inject(LocalizationService);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly initialLoading = signal(true);
  readonly tableLoading = signal(false);
  readonly error = signal<string | null>(null);
  readonly noPermission = signal(false);
  readonly rows = signal<NotificationMessageSummary[]>([]);
  readonly selectedRows = signal<NotificationMessageSummary[]>([]);
  readonly totalCount = signal(0);
  readonly tableFirst = signal(0);

  readonly pageSize = 25;
  filters = { search: '', channel: null as string | null, category: null as string | null, status: null as string | null, providerKey: null as string | null, fromDate: null as string | null, toDate: null as string | null };

  readonly channelOptions = [
    { label: 'Email', value: 'Email' },
    { label: 'SMS', value: 'Sms' }
  ];
  readonly categoryOptions = [
    { label: 'Transactional', value: 'Transactional' },
    { label: 'Service', value: 'Service' },
    { label: 'Marketing', value: 'Marketing' }
  ];
  readonly statusOptions = [
    { label: 'Queued', value: 'Queued' },
    { label: 'Processing', value: 'Processing' },
    { label: 'Sent', value: 'Sent' },
    { label: 'Failed', value: 'Failed' },
    { label: 'Cancelled', value: 'Cancelled' },
    { label: 'Scheduled', value: 'Scheduled' }
  ];
  readonly providerOptions = [
    { label: 'SMTP', value: 'smtp' },
    { label: 'Gmail', value: 'gmail-oauth' },
    { label: 'Kavenegar', value: 'kavenegar' }
  ];

  ngOnInit(): void {
    this.restoreQueryState();
    this.liveUpdateService.events$
      .pipe(
        filter(event => event.eventType === LiveUpdateEventTypes.notificationMessagesChanged),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));

    void this.setupLiveSubscription();
    this.load(true);
  }

  t(key: string): string { return this.i18n.translate(key); }

  channelLabel(channel: unknown): string {
    const normalized = normalizeNotificationChannel(channel as never);
    return this.t(`notifications.enums.channel.${normalized}`);
  }

  categoryLabel(category: unknown): string {
    return this.t(`notifications.enums.category.${normalizeNotificationCategory(category as never) ?? 'Other'}`);
  }

  statusLabel(status: string): string {
    const key = `notifications.enums.messageStatus.${status}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  refresh(): void { this.load(true); }

  applyFilters(): void {
    this.tableFirst.set(0);
    this.syncQueryState();
    this.load(true);
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize;
    this.tableFirst.set(first);
    this.load(false, first, rows);
  }

  openDetail(id: string): void {
    void this.router.navigate(['/notifications/messages', id]);
  }

  private load(showInitial: boolean, skip = this.tableFirst(), take = this.pageSize): void {
    if (showInitial) this.initialLoading.set(true);
    this.tableLoading.set(true);
    this.error.set(null);

    const fromUtc = this.filters.fromDate ? `${this.filters.fromDate}T00:00:00Z` : undefined;
    const toUtc = this.filters.toDate ? `${this.filters.toDate}T23:59:59Z` : undefined;

    this.api.listMessages({
      messageKey: this.filters.search.trim() || undefined,
      channel: this.filters.channel as never,
      category: this.filters.category as never,
      status: this.filters.status ?? undefined,
      providerKey: this.filters.providerKey,
      fromUtc,
      toUtc,
      skip,
      take
    }).pipe(finalize(() => {
      this.initialLoading.set(false);
      this.tableLoading.set(false);
    })).subscribe({
      next: page => {
        this.rows.set(page.items);
        this.totalCount.set(page.totalCount);
      },
      error: err => {
        if (err?.status === 403) {
          this.noPermission.set(true);
          return;
        }
        this.error.set(this.t('common.error'));
      }
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) return;
    await this.liveUpdateService.subscribe(notificationMessagesTopic(tenantId));
  }

  private restoreQueryState(): void {
    const q = this.route.snapshot.queryParamMap;
    this.filters.search = q.get('search') ?? '';
    this.filters.channel = q.get('channel');
    this.filters.category = q.get('category');
    this.filters.status = q.get('status');
    this.filters.providerKey = q.get('providerKey');
    this.filters.fromDate = q.get('fromDate');
    this.filters.toDate = q.get('toDate');
    this.tableFirst.set(Number(q.get('skip') ?? 0));
  }

  private syncQueryState(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        search: this.filters.search || null,
        channel: this.filters.channel,
        category: this.filters.category,
        status: this.filters.status,
        providerKey: this.filters.providerKey,
        fromDate: this.filters.fromDate,
        toDate: this.filters.toDate,
        skip: this.tableFirst() || null
      },
      queryParamsHandling: 'merge'
    });
  }
}
